/**
 * Darstellung einer Mahnung als KIND ihrer Rechnung.
 *
 * Kernregel: die Betragsspalte zeigt NIE den Mahnbetrag. Eine Sammelmahnung
 * ueber 107 Rechnungen erscheint unter jeder davon — stuende dort der
 * Gesamtbetrag, laese man ihn 107-mal als Forderung. Gezeigt wird die anteilige
 * Nebenforderung, ausdruecklich als „anteilig" gelabelt, damit sie niemand in
 * einen Zahllauf uebernimmt.
 */
import { AlertTriangle, Eye, FileText } from 'lucide-react';
import type { ReminderChild } from './useInvoiceRows';

const formatCurrency = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '-'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

const formatDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

export const isReminderDone = (status: string) => status === 'paid' || status === 'resolved';

export function ReminderStatusCell({ rem }: { rem: ReminderChild }) {
  const done = isReminderDone(rem.status);
  return (
    <div className="flex items-center gap-2 pl-6">
      <span className="text-xs text-gray-400">↳</span>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          done ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
        }`}
      >
        {rem.status === 'resolved'
          ? '✓ Erledigt'
          : rem.status === 'paid'
            ? '✓ Bezahlt'
            : `Mahnung${rem.reminder_level ? ` St.${rem.reminder_level}` : ''}`}
      </span>
    </div>
  );
}

export function ReminderNumberCell({
  rem,
  onOpenDetail,
}: {
  rem: ReminderChild;
  onOpenDetail: (id: number) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-3">
        {rem.linked_count > 1 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
            Sammelmahnung
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onOpenDetail(rem.id); }}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 transition-colors"
        >
          <Eye className="w-3 h-3" />
          Details
        </button>
        {rem.original_file_path && (
          <a
            href={`/api/uploads/${rem.original_file_path}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
          >
            <FileText className="w-3 h-3" />
            Beleg
          </a>
        )}
      </div>
      {rem.linked_count > 1 && (
        <span className="text-[11px] text-gray-400">
          {rem.linked_count} Rechnungen · Gesamtforderung {formatCurrency(rem.gross_amount)}
          {rem.fees_amount !== null && <> · Nebenforderung {formatCurrency(rem.fees_amount)}</>}
        </span>
      )}
    </div>
  );
}

export function ReminderAmountCell({ rem }: { rem: ReminderChild }) {
  if (rem.fees_amount === null || rem.fees_amount === undefined) {
    return (
      <span
        className="text-sm text-right block text-gray-400 italic"
        title="Nebenforderung war im Beleg nicht ermittelbar — im Detail nachtragen"
      >
        Nebenford. offen
      </span>
    );
  }
  const share = rem.fee_share ?? rem.fees_amount;
  return (
    <div className="text-right">
      <span className="text-sm font-medium text-orange-700">+ {formatCurrency(share)}</span>
      <span className="block text-[10px] text-gray-400">
        Nebenforderung{rem.linked_count > 1 ? ' (anteilig)' : ''}
      </span>
    </div>
  );
}

export function ReminderDateCell({ rem }: { rem: ReminderChild }) {
  return <span className="text-sm text-gray-500">{formatDate(rem.invoice_date)}</span>;
}

export function ReminderDueCell({ rem }: { rem: ReminderChild }) {
  return <span className="text-sm text-gray-500">{formatDate(rem.due_date)}</span>;
}

export function ReminderSupplierCell({ rem }: { rem: ReminderChild }) {
  return (
    <span className="text-sm text-gray-500">
      {rem.description || 'Mahnung'}
      {rem.fees_source === 'manual' && (
        <span
          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700"
          title="Nebenforderung von Hand gepflegt"
        >
          manuell
        </span>
      )}
    </span>
  );
}

/** Badge an der ELTERN-Zeile: wie viele Mahnungen und welche Stufe. */
export function ParentReminderBadge({ count, maxLevel }: { count: number; maxLevel: number | null }) {
  if (!count) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 text-orange-800"
      title={`${count} Mahnung(en) zu dieser Rechnung`}
    >
      <AlertTriangle className="w-3 h-3" />
      {count} {count === 1 ? 'Mahnung' : 'Mahnungen'}
      {maxLevel ? ` · St.${maxLevel}` : ''}
    </span>
  );
}
